const rootUrl = () => window.Shopify?.routes?.root || '/';

/* ---------------------------------------------------------------------------
   Add to cart (works for forms added later, such as related products)
--------------------------------------------------------------------------- */

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-add-to-cart]');
  if (!form) return;

  const button = form.querySelector('[data-add-to-cart-button]');
  const status = form.querySelector('[data-add-to-cart-status]');
  if (!button || !status) return;

  event.preventDefault();
  if (button.disabled) return;

  const originalLabel = button.textContent.trim();
  const addingLabel = button.dataset.addingLabel || originalLabel;
  const addedLabel = button.dataset.addedLabel || originalLabel;

  button.disabled = true;
  button.textContent = addingLabel;
  status.textContent = '';
  status.classList.remove('is-error');

  try {
    const response = await fetch(`${form.action}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: new FormData(form)
    });
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.description || result.message || 'Unable to add this item.');
    }

    const cartResponse = await fetch(`${rootUrl()}cart.js`, {
      headers: {
        Accept: 'application/json'
      }
    });
    const cart = await cartResponse.json();

    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });

    button.textContent = addedLabel;
    status.textContent = addedLabel;

    window.setTimeout(() => {
      button.textContent = originalLabel;
      button.disabled = false;
    }, 1400);
  } catch (error) {
    status.textContent = error.message;
    status.classList.add('is-error');
    button.textContent = originalLabel;
    button.disabled = false;
  }
});

/* ---------------------------------------------------------------------------
   Quantity steppers
--------------------------------------------------------------------------- */

document.addEventListener('click', (event) => {
  const stepButton = event.target.closest('[data-quantity-step]');
  if (!stepButton) return;

  const input = stepButton.closest('[data-quantity]')?.querySelector('input');
  if (!input) return;

  const min = Number(input.min) || 1;
  const next = Math.max(min, (Number(input.value) || min) + Number(stepButton.dataset.quantityStep));
  input.value = next;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------------------------------------------------------------------------
   Product page: variants
--------------------------------------------------------------------------- */

const variantRequests = new WeakMap();

const getVariants = (section) => {
  if (!section._variants) {
    const data = section.querySelector('[data-product-variants]');
    section._variants = data ? JSON.parse(data.textContent) : [];
  }
  return section._variants;
};

const selectedOptions = (picker) => {
  const values = [];
  picker.querySelectorAll('select[data-option-index]').forEach((select) => {
    values[Number(select.dataset.optionIndex)] = select.value;
  });
  picker.querySelectorAll('input[data-option-index]:checked').forEach((input) => {
    values[Number(input.dataset.optionIndex)] = input.value;
  });
  return values;
};

const updateVariant = async (section, variant) => {
  section.querySelectorAll('[data-variant-id-input]').forEach((input) => {
    input.value = variant.id;
  });

  const url = new URL(section.dataset.productUrl, window.location.origin);
  url.searchParams.set('variant', variant.id);
  if (!window.Shopify?.designMode) {
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  if (variant.media) showMedia(section, variant.media, true);

  const requestId = (variantRequests.get(section) || 0) + 1;
  variantRequests.set(section, requestId);

  url.searchParams.set('section_id', section.dataset.sectionId);
  const html = await fetch(url).then((response) => response.text());
  if (variantRequests.get(section) !== requestId) return;

  const fresh = new DOMParser().parseFromString(html, 'text/html');
  const focusedId = document.activeElement?.id;

  section.querySelectorAll('[data-swap][id]').forEach((element) => {
    const replacement = fresh.getElementById(element.id);
    if (!replacement) return;
    element.innerHTML = replacement.innerHTML;
    if ('disabled' in element) element.disabled = replacement.hasAttribute('disabled');
  });

  if (focusedId) document.getElementById(focusedId)?.focus();
};

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-option-index]');
  const section = input?.closest('[data-product-section]');
  if (!section) return;

  const picker = input.closest('[data-variant-picker]');
  const variants = getVariants(section);
  const values = selectedOptions(picker);
  const changedIndex = Number(input.dataset.optionIndex);

  // If the exact combination doesn't exist, keep the option the shopper just
  // picked and fall back to the closest variant that has it.
  const variant =
    variants.find((item) => item.options.every((value, index) => value === values[index])) ||
    variants.find((item) => item.options[changedIndex] === input.value && item.available) ||
    variants.find((item) => item.options[changedIndex] === input.value);

  if (variant) updateVariant(section, variant);
});

/* ---------------------------------------------------------------------------
   Product page: gallery, thumbnails, lightbox, share
--------------------------------------------------------------------------- */

const setActiveThumb = (gallery, mediaId) => {
  gallery.querySelectorAll('[data-thumb-for]').forEach((thumb) => {
    const active = thumb.dataset.thumbFor === String(mediaId);
    thumb.classList.toggle('is-active', active);
    if (active) thumb.setAttribute('aria-current', 'true');
    else thumb.removeAttribute('aria-current');
  });
};

const showMedia = (scope, mediaId, fromVariant = false) => {
  const gallery = scope.matches('[data-gallery]') ? scope : scope.querySelector('[data-gallery]');
  const track = gallery?.querySelector('[data-gallery-track]');
  const item = track?.querySelector(`[data-media-id="${mediaId}"]`);
  if (!item) return;

  const isSlider = track.scrollWidth > track.clientWidth + 1;

  if (isSlider) {
    track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
  } else if (fromVariant) {
    // Stacked and grid layouts: bring the variant's image to the top.
    track.prepend(item);
  } else {
    item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  setActiveThumb(gallery, mediaId);
};

document.addEventListener('click', (event) => {
  const thumb = event.target.closest('[data-thumb-for]');
  if (thumb) {
    showMedia(thumb.closest('[data-gallery]'), thumb.dataset.thumbFor);
    return;
  }

  const zoom = event.target.closest('[data-zoom-src]');
  if (zoom) {
    const dialog = zoom.closest('[data-product-section]')?.querySelector('[data-lightbox]');
    const image = dialog?.querySelector('[data-lightbox-image]');
    if (!dialog || !image) return;
    image.src = zoom.dataset.zoomSrc;
    image.alt = zoom.dataset.zoomAlt || '';
    dialog.showModal();
    return;
  }

  const lightbox = event.target.closest('[data-lightbox]');
  if (lightbox && (event.target.closest('[data-lightbox-close]') || event.target === lightbox || event.target.matches('[data-lightbox-image]'))) {
    lightbox.close();
    return;
  }

  const share = event.target.closest('[data-share]');
  if (share) {
    const url = share.dataset.shareUrl;
    const label = share.querySelector('[data-share-label]');

    if (navigator.share) {
      navigator.share({ title: share.dataset.shareTitle, url }).catch(() => {});
      return;
    }

    navigator.clipboard?.writeText(url).then(() => {
      if (!label) return;
      const original = label.textContent;
      label.textContent = share.dataset.copiedLabel;
      window.setTimeout(() => {
        label.textContent = original;
      }, 2000);
    });
  }
});

const initGallery = (gallery) => {
  const track = gallery.querySelector('[data-gallery-track]');
  if (!track || track.dataset.ready) return;
  track.dataset.ready = 'true';

  let frame;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      if (track.scrollWidth <= track.clientWidth + 1) return;
      const index = Math.round(track.scrollLeft / track.clientWidth);
      const item = track.children[index];
      if (item?.dataset.mediaId) setActiveThumb(gallery, item.dataset.mediaId);
    });
  }, { passive: true });
};

/* ---------------------------------------------------------------------------
   Related products
--------------------------------------------------------------------------- */

const initRelatedProducts = async (container) => {
  if (!container.dataset.url || container.dataset.loaded) return;
  container.dataset.loaded = 'true';

  try {
    const html = await fetch(container.dataset.url).then((response) => response.text());
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-related-products]');
    if (fresh && fresh.innerHTML.trim()) container.innerHTML = fresh.innerHTML;
  } catch (error) {
    container.hidden = true;
  }
};

/* ---------------------------------------------------------------------------
   Header: mobile menu and dropdowns
--------------------------------------------------------------------------- */

document.addEventListener('click', (event) => {
  document.querySelectorAll('[data-mobile-menu][open], .nav-dropdown[open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-mobile-menu][open], .nav-dropdown[open]').forEach((details) => {
    details.removeAttribute('open');
    details.querySelector('summary')?.focus();
  });
});

document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!details.matches?.('.nav-dropdown') || !details.open) return;
  document.querySelectorAll('.nav-dropdown[open]').forEach((other) => {
    if (other !== details) other.removeAttribute('open');
  });
}, true);

window.matchMedia('(min-width: 961px)').addEventListener('change', (query) => {
  if (query.matches) {
    document.querySelectorAll('[data-mobile-menu][open]').forEach((menu) => menu.removeAttribute('open'));
  }
});

/* ---------------------------------------------------------------------------
   Announcement bar rotation
--------------------------------------------------------------------------- */

const initAnnouncement = (bar) => {
  if (bar.dataset.ready) return;
  bar.dataset.ready = 'true';

  const messages = [...bar.querySelectorAll('.announcement-message')];
  if (messages.length < 2) return;

  let index = 0;
  let paused = false;
  bar.addEventListener('mouseenter', () => { paused = true; });
  bar.addEventListener('mouseleave', () => { paused = false; });

  bar._timer = window.setInterval(() => {
    if (paused) return;
    messages[index].hidden = true;
    index = (index + 1) % messages.length;
    messages[index].hidden = false;
  }, Number(bar.dataset.interval) || 5000);
};

/* ---------------------------------------------------------------------------
   Sell / trade CSV upload
--------------------------------------------------------------------------- */

const initCsvUpload = (form) => {
  const input = form.querySelector('[data-csv-input]');
  const fileName = form.querySelector('[data-file-name]');

  if (!input || !fileName || input.dataset.ready) return;
  input.dataset.ready = 'true';

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    input.setCustomValidity('');

    if (!file) {
      fileName.textContent = 'No file selected';
      return;
    }

    const isCsv = file.name.toLowerCase().endsWith('.csv');
    const maxBytes = Number(input.dataset.maxBytes);

    if (!isCsv) {
      input.setCustomValidity(input.dataset.invalidMessage || 'Please select a CSV file.');
    } else if (maxBytes && file.size > maxBytes) {
      input.setCustomValidity(input.dataset.tooLargeMessage || 'That file is too large.');
    }

    fileName.textContent = file.name;

    if (!input.checkValidity()) input.reportValidity();
  });
};

/* ---------------------------------------------------------------------------
   Startup (also re-runs when a section is edited in the theme editor)
--------------------------------------------------------------------------- */

const init = (root = document) => {
  root.querySelectorAll('[data-scroll]').forEach((element) => {
    if (element.dataset.ready) return;
    element.dataset.ready = 'true';
    element.addEventListener('click', (event) => {
      const id = element.getAttribute('href');

      if (id?.startsWith('#')) {
        event.preventDefault();
        document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });

  root.querySelectorAll('[data-gallery]').forEach(initGallery);
  root.querySelectorAll('[data-related-products]').forEach(initRelatedProducts);
  root.querySelectorAll('[data-announcement-rotator]').forEach(initAnnouncement);
  root.querySelectorAll('[data-csv-upload]').forEach(initCsvUpload);
};

document.addEventListener('DOMContentLoaded', () => init());
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-announcement-rotator]').forEach((bar) => window.clearInterval(bar._timer));
});
document.addEventListener('shopify:block:select', (event) => {
  const message = event.target.closest('.announcement-message');
  if (!message) return;
  message.parentElement.querySelectorAll('.announcement-message').forEach((item) => {
    item.hidden = item !== message;
  });
});
